import {
  ChangeEvent,
  ElementType,
  FC,
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useInView } from 'react-intersection-observer';
import { FormattedMessage, useIntl } from 'react-intl';
import { useResizeDetector } from 'react-resize-detector';

import FilterList from '@mui/icons-material/FilterList';
import {
  Avatar,
  Box,
  Button,
  Chip as MuiChip,
  ChipProps,
  Grid,
  Icon,
  Pagination,
  Stack,
  Tab,
  Tabs,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';

import clsx from 'clsx';
import _uniqBy from 'lodash/uniqBy';

import { convertToTaxonomyCase, EVENTS, useTrack } from '@arrived/analytics';
import { useHideIntercomLauncher, useOfferingFilters } from '@arrived/contexts';
import { PalmTree } from '@arrived/images';
import { useOfferingSearchInfiniteQuery, useOfferingSearchQuery } from '@arrived/queries';

import { Loading } from '../../Loading';
import { LoadingBackdrop } from '../../LoadingBackdrop';
import { FilterDrawer } from '../Filters';
import { NotifyMeButtonContextProvider } from './NotifyMeButtonContext';
import { PropertyGridCardProps } from './PropertyGridCard';

import { NavigationBarContext } from '../../navigation';
import { PropertyGridBanner } from './banner';
import { useOfferingPageSize } from './useOfferingPageSize';

const Chip = ({ sx, ...rest }: ChipProps) => (
  <MuiChip
    sx={{
      textTransform: 'initial',
      fontWeight: ({ fontWeights }) => fontWeights.medium,
      '&.active': { backgroundColor: ({ palette }) => palette.background.iceGray },
      '&.active svg path': { fill: ({ palette }) => palette.tertiary[500] },
      ...sx,
    }}
    {...rest}
  />
);

// TODO: This is gross, I do not like this.
const filterIconMap: Record<string, JSX.Element> = {
  'palm-tree': <PalmTree />,
};

export const FilterIcon = ({ name }: { name: string }) => {
  const IconComponent = filterIconMap[name];
  return IconComponent || <Icon>{name}</Icon>;
};

interface PropertyGridProps<D extends ElementType> {
  PropertyGridCardComponent: FC<Omit<PropertyGridCardProps<D>, 'onInvestNowClick'>>;
  filterOffset?: number;
}

export const PropertyGrid = <D extends ElementType>({
  PropertyGridCardComponent,
  filterOffset = 0,
}: PropertyGridProps<D>) => {
  const { ref: inViewRef, inView } = useInView();
  const { featuredFilters, featuredFilterIdx, offeringFilter, setFeaturedFilterIdx, setOfferingFilter, filterCount } =
    useOfferingFilters();
  const { offset: navBarOffset, transition } = useContext(NavigationBarContext);

  const pageSize = useOfferingPageSize();

  const [page, setPage] = useState(1);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(true);

  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('md'));

  // Need to sort by both `propertyRank` and `id` so that we can guarantee idempotent return values for each page (under
  // the assumption that nothing about the underlying Offerings changed). This prevents a bug that we've seen where
  // specifically with the infinite scroll we get duplicated key errors causing rendering issues.
  const offeringState = useOfferingSearchQuery(
    { ...offeringFilter, sort: 'propertyRank:desc,id:desc', size: pageSize, page },
    { enabled: !isMobile }
  );
  const offeringInfiniteState = useOfferingSearchInfiniteQuery(
    { ...offeringFilter, sort: 'propertyRank:desc,id:desc', size: 10 },
    { enabled: isMobile }
  );

  useEffect(() => {
    if (inView) {
      offeringInfiniteState.fetchNextPage();
    }
  }, [inView]);

  const pageCount = useMemo(
    () => (offeringState.isSuccess ? Math.ceil(offeringState.data.pagination.totalResults / pageSize) : undefined),
    [offeringState.data, pageSize]
  );

  useEffect(() => {
    if (pageCount != null && page > pageCount) {
      setPage(Math.max(1, pageCount));
    }
  }, [page, pageCount]);

  // We don't want to show the loading overlay every time we're fetching, only when it starts to take some time. This
  // effect will only fire when the isFetching attribute on the offerings query changes such that if it's fetching,
  // we'll set a timeout that will fire later to set the overlay state to true, if fetching changes again, it'll clear
  // that timeout and set the overlay to false to make sure that it's not showing.
  useEffect(() => {
    if (!(offeringState.isFetching || offeringInfiniteState.isFetching)) {
      setShowLoadingOverlay(false);
      return;
    }

    const timeoutId = setTimeout(() => setShowLoadingOverlay(true), 250);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [offeringInfiniteState.isFetching, offeringState.isFetching]);

  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const { ref } = useResizeDetector({ handleHeight: true });
  const track = useTrack();
  const intl = useIntl();

  useHideIntercomLauncher(isFilterDrawerOpen);

  const handleTabsOnChange = useCallback((_: SyntheticEvent, value: number) => {
    setFeaturedFilterIdx(value);
  }, []);

  const handleOnFilterClick = useCallback(() => {
    setIsFilterDrawerOpen(true);
  }, []);

  const handleOnFilterDrawerClose = useCallback(() => {
    setIsFilterDrawerOpen(false);
  }, []);

  const handleClearAllOnClick = useCallback(() => setOfferingFilter(), [setOfferingFilter]);

  useEffect(() => {
    setPage(1);
  }, [offeringFilter]);

  const handlePageOnChange = useCallback(
    (_: ChangeEvent<unknown>, page: number) => {
      setPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [setPage]
  );

  useEffect(() => {
    if (isMobile) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [offeringFilter]);

  const offeringCount = useMemo(() => {
    if (isMobile) {
      if (offeringInfiniteState.isLoading) {
        return undefined;
      }
      return offeringInfiniteState.data && offeringInfiniteState.data?.pages.length >= 1
        ? offeringInfiniteState.data.pages[0].pagination.totalResults
        : undefined;
    } else {
      return offeringState.isLoading ? undefined : offeringState.data?.pagination.totalResults;
    }
  }, [offeringInfiniteState, offeringState]);

  const offerings = useMemo(() => {
    return isMobile
      ? // TODO: For some reason multiple pages are returning repeated offerings on search when queried rapidly, if that gets
        //  fixed then we can remove this
        _uniqBy(offeringInfiniteState.data?.pages.flatMap((page) => page.data) ?? [], 'id')
      : offeringState.data?.data ?? [];
  }, [isMobile, offeringState.data?.data, offeringInfiniteState.data?.pages]);

  return (
    <NotifyMeButtonContextProvider>
      <Box width="100%" flexGrow={1} display="flex" flexDirection="column">
        <>
          <Tabs
            data-e2e="featured-filters-mobile"
            onChange={handleTabsOnChange}
            ref={ref}
            variant="scrollable"
            scrollButtons
            allowScrollButtonsMobile
            sx={{
              backgroundColor: ({ palette }) => palette.neutrals.white,
              display: { xs: 'flex', md: 'none' },
              zIndex: 1,
              top: 0,
              '& .MuiTabs-flexContainer': {
                gap: 3,
              },
              '& .MuiTabs-indicator': {
                backgroundColor: ({ palette }) => palette.darkened.midnight,
              },
            }}
            value={featuredFilterIdx ?? false}
          >
            {featuredFilters.map((featuredFilter, idx) => {
              const { label, labelIcon } = featuredFilter;

              return (
                <Tab
                  key={idx}
                  label={
                    <Box display="flex" alignItems="center">
                      <FormattedMessage {...label} />
                      {labelIcon && (
                        <Box component="span" pl={1}>
                          <FilterIcon name={labelIcon} />
                        </Box>
                      )}
                    </Box>
                  }
                  value={idx}
                  sx={(theme) => ({
                    borderBottomColor: theme.palette.neutrals.gray,
                    fontSize: { xs: theme.typography.body1.fontSize },
                    fontWeight: 'medium',
                    color: theme.palette.darkened.midnight,
                    px: 0,
                    minWidth: 'auto',
                    '&.Mui-selected': {
                      color: theme.palette.darkened.midnight,
                      fontWeight: 'bold',
                    },
                  })}
                  onClick={() => {
                    track(EVENTS.OFFERING_FEATURED_FILTER_CLICKED, {
                      feature_name: convertToTaxonomyCase(intl.formatMessage(label)),
                    });
                  }}
                />
              );
            })}
          </Tabs>
          <PropertyGridBanner />
          <Stack
            alignItems="center"
            direction="row"
            spacing={{ xs: 0, md: 3 }}
            sx={{
              backgroundColor: ({ palette }) => palette.neutrals.white,
              px: { xs: 0, md: 5, lg: 8 },
              py: { xs: 0, md: 3 },
              top: filterOffset + navBarOffset,
              zIndex: 2,
            }}
            style={{ transition: `top ${transition}` }}
            position={{ xs: 'sticky', md: 'static' }}
          >
            <Stack
              direction="row"
              data-e2e="featured-filters-desktop"
              spacing={1}
              display={{ xs: 'none', md: 'flex' }}
              flex="1 1 auto"
              sx={{ overflowX: 'auto', py: 1 }}
            >
              {featuredFilters.map((featuredFilter, idx) => {
                const { label, labelIcon } = featuredFilter;
                const isActive = idx === featuredFilterIdx;

                const handleOnClick = () => {
                  setFeaturedFilterIdx(idx);

                  track(EVENTS.OFFERING_FEATURED_FILTER_CLICKED, {
                    feature_name: convertToTaxonomyCase(intl.formatMessage(label)),
                  });
                };

                return (
                  <Chip
                    className={clsx('filter-chip', { active: isActive })}
                    clickable
                    label={
                      <Typography
                        display="flex"
                        alignItems="center"
                        fontWeight={isActive ? 'semibold' : 'normal'}
                        color={({ palette }) => (isActive ? palette.darkened.midnight : palette.neutrals.gray)}
                      >
                        <FormattedMessage {...label} />
                        {labelIcon && (
                          <Box component="span" pl={1}>
                            <FilterIcon name={labelIcon} />
                          </Box>
                        )}
                      </Typography>
                    }
                    onClick={handleOnClick}
                    key={idx}
                    sx={{ fontWeight: 'normal' }}
                  />
                );
              })}
            </Stack>
            <Chip
              clickable
              data-e2e="filters-slideout-button"
              icon={<FilterList fontSize="small" />}
              label={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography component="span" variant="body1.medium">
                    <FormattedMessage id="filters" defaultMessage="Filters" />
                  </Typography>
                  {filterCount > 0 && (
                    <Avatar
                      sx={(theme) => ({
                        backgroundColor: ({ palette }) => palette.secondary.main,
                        fontSize: theme.typography.body2.fontSize,
                        fontWeight: 'semibold',
                        height: '20px',
                        width: '20px',
                      })}
                    >
                      {filterCount}
                    </Avatar>
                  )}
                </Stack>
              }
              onClick={handleOnFilterClick}
              sx={{
                borderLeftWidth: { xs: 0, md: '1px' },
                borderRightWidth: { xs: 0, md: '1px' },
                borderColor: ({ palette }) => palette.neutrals.platinum,
                color: ({ palette }) => palette.darkened.midnight,
                py: { xs: 3, md: 0 },
                width: { xs: '100%', md: 'auto' },
                flex: '0 1 auto',
              }}
              variant="outlined"
            />
          </Stack>
          <Grid
            container
            data-e2e="property-cards-container"
            flexGrow={1}
            spacing={{ xs: 3, md: 4 }}
            px={{ xs: 4, md: 5, lg: 8 }}
            py={4}
            sx={{ backgroundColor: 'background.gray' }}
          >
            {!isMobile && showLoadingOverlay && <LoadingBackdrop open />}
            {offeringCount === 0 &&
              ((offeringState.isSuccess && !isMobile) || (offeringInfiniteState.isSuccess && isMobile)) && (
                <Grid
                  item
                  container
                  data-e2e="property-card"
                  direction="column"
                  justifyItems="center"
                  alignItems="center"
                  spacing={3}
                  sx={{ grow: 1 }}
                >
                  <Grid item>
                    <Typography variant="h5.semibold" textAlign="center" mt={4}>
                      <FormattedMessage id="no-properties-header" defaultMessage="No Current Matches" />
                    </Typography>
                    <Typography variant="body1" textAlign="center" py={3} maxWidth="75vh">
                      <FormattedMessage
                        id="no-properties-message"
                        defaultMessage="We don’t have any available properties that match these specific filters at the moment,
  though we are constantly adding more properties!"
                      />
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Stack spacing={2} direction={{ base: 'column', md: 'row' }}>
                      <Button onClick={handleClearAllOnClick}>
                        <FormattedMessage id="no-properties-button-reset" defaultMessage="Clear All" />
                      </Button>
                      <Button onClick={handleOnFilterClick}>
                        <FormattedMessage id="no-properties-button-change" defaultMessage="Adjust Filters" />
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              )}
            {offerings.map((offering, index) => {
              return (
                <Grid
                  item
                  sm={12}
                  md={6}
                  lg={4}
                  xl={3}
                  flexGrow={1}
                  data-e2e="property-card"
                  key={offering.id}
                  maxWidth={{ xs: '500px', md: 'unset' }}
                  mx={{ xs: 'auto', md: 'unset' }}
                  ref={isMobile && index === offerings.length - 1 ? inViewRef : undefined}
                >
                  <PropertyGridCardComponent elevation={0} offering={offering} />
                </Grid>
              );
            })}
            {isMobile && (
              <>
                {showLoadingOverlay && (
                  <Grid item xs={12} mx="auto" height="100px">
                    <Loading />
                  </Grid>
                )}
                {!offeringInfiniteState.hasNextPage && offeringCount !== 0 && (
                  <Box display="flex" alignItems="center" mx={2} mt={2}>
                    <Typography variant="body2" textAlign="center">
                      <FormattedMessage
                        id="no-more-properties-message"
                        defaultMessage="We don’t have any more properties that match these specific filters at the moment."
                      />
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Grid>
          {offeringState.isSuccess && (pageCount ?? 0) >= 1 && (
            <Box
              pb={4}
              display={{ xs: 'none', md: 'flex' }}
              justifyContent="center"
              sx={{ backgroundColor: 'background.gray' }}
            >
              <Pagination count={pageCount} onChange={handlePageOnChange} page={page} size="large" />
            </Box>
          )}
        </>
        <FilterDrawer
          anchor={isMobile ? 'bottom' : 'right'}
          close={handleOnFilterDrawerClose}
          onClose={handleOnFilterDrawerClose}
          open={isFilterDrawerOpen}
          offeringCount={offeringCount}
        />
      </Box>
    </NotifyMeButtonContextProvider>
  );
};
